import { noop } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { IM_PAYING_MYSELF_TEXT } from '@zocdoc-frontend-common/components-insurancepicker/constants';
import { slate40, slate70 } from '@zocdoc-mezzanine/foundations-colors';
import { Button } from '@zocdoc-frontend-common/components-button';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { linkStyle } from '@zocdoc-frontend-common/components-link';
import { FormRow } from '@zocdoc-frontend-common/styles-grid';
import {
    MetricsClickable,
    withCustomMetrics,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc/frontend-metrics';
import { Link } from '@zocdoc-frontend-common/components-link';

import AppointmentDetailsForm from '../AppointmentDetailsForm';
import { InsuranceCardSection } from '../InsuranceCardSection';
import { ShareMyInsuranceCardWrapper } from '../InsuranceCardSection/InsuranceCardSectionView';
import { DoctorNotes } from '../DoctorNotes';
import EmergencyNotice from '../EmergencyNotice';
import Expandable from './Expandable';

class BaseStaticReviewAndBook extends Component {
    static propTypes = {
        appointmentDetails: PropTypes.object,
        expandedPatientSection: PropTypes.element,
        expandedInsuranceSection: PropTypes.element,
        expandedAppointmentSection: PropTypes.element,
        formattedAppointmentTime: PropTypes.string,
        hasUsingInsuranceRequirement: PropTypes.bool,
        hasAppointmentTimeError: PropTypes.bool,
        insuranceCarrierId: PropTypes.string,
        insuranceCarrierName: PropTypes.string,
        insurancePlanId: PropTypes.string,
        insurancePlanName: PropTypes.string,
        isNewPatient: PropTypes.bool,
        mainPatientName: PropTypes.string,
        onBookAppointmentClick: PropTypes.func,
        phoneNumber: PropTypes.string,
        phoneNumberRequired: PropTypes.bool,
        procedureName: PropTypes.string,
        sendMetric: PropTypes.func,
        shouldDisableMemberIdField: PropTypes.bool,
        shouldShowEmergencyNotice: PropTypes.bool,

        // for screenshot testing
        __forceIsEditingDoctorNotes: PropTypes.bool,
        __forceIsEditingMemberId: PropTypes.bool,
    };

    static defaultProps = {
        appointmentDetails: {},
        sendMetric: noop,
        __forceIsEditingDoctorNotes: false,
        __forceIsEditingMemberId: false,
    };

    /* If this experiment is ever merged, we should move the render functions
       for the form in reviewAndBook/view.js (expandedPatientSection etc.),
       into this one & merge the two components into one */
    constructor(props) {
        super(props);
        // Show Patient section on mount if they do not have a phone number
        // Show Insurance section on mount if they do not have insurance
        // Show Appointment Info section if their time is no longer available
        // If shown on mount, don't fade in
        const { hasAppointmentTimeError, phoneNumberRequired } = this.props;
        this._openPatientSectionOnMount = phoneNumberRequired;
        this._openInsuranceSectionOnMount = !this.hasInsurance();
        this._openAppointmentSectionOnMount = hasAppointmentTimeError;
        this._didSendApptTimeMetric = false;

        this.state = {
            showPatientSection: this._openPatientSectionOnMount,
            showInsuranceSection: this._openInsuranceSectionOnMount,
            showAppointmentSection: this._openAppointmentSectionOnMount,
            isEditingDoctorNotes: this.props.__forceIsEditingDoctorNotes,
            isEditingMemberId: this.props.__forceIsEditingMemberId,
        };

        this._staticPatientSectionRef = React.createRef();
        this._staticPatientSectionHeight = 'auto';
        this._staticInsuranceSectionRef = React.createRef();
        this._staticInsuranceSectionHeight = 'auto';
        this._staticAppointmentSectionRef = React.createRef();
        this._staticAppointmentSectionHeight = 'auto';
    }

    componentDidMount() {
        const { hasAppointmentTimeError, sendMetric } = this.props;
        const element = 'StaticReviewAndBook';
        sendMetric({
            element,
            eventDetails: {
                PatientSectionOpened: !!this._openPatientSectionOnMount,
                InsuranceSectionOpened: !!this._openInsuranceSectionOnMount,
                AppointmentSectionOpened: !!this._openAppointmentSectionOnMount,
            },
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.VIEW,
        });
        if (hasAppointmentTimeError) {
            // if shown appt time error, send this metric
            this._didSendApptTimeMetric = true;
            this.sendAppointmentTimeMetric();
        }
    }

    componentDidUpdate(prevProps) {
        // account for possible race condition on button click vs.
        // api not returning in time
        if (
            !prevProps.hasAppointmentTimeError &&
            this.props.hasAppointmentTimeError
        ) {
            this.setState({ showAppointmentSection: true });
            if (!this._didSendApptTimeMetric) {
                this._didSendApptTimeMetric = true;
                this.sendAppointmentTimeMetric();
            }
        }
    }

    sendAppointmentTimeMetric = () => {
        this.props.sendMetric({
            section: 'ReviewAndBook',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.VIEW,
            component: 'ErrorMessageBox',
            element: 'AppointmentTimeNoLongerAvailable',
            category: 'Booking - Appt Info',
            action: 'Appointment No Longer Available Error from Interim Step',
        });
    };

    render() {
        return (
            <Container data-test="static-review-and-book">
                {this.renderPatientSection()}
                {this.renderInsuranceSection()}
                {this.renderAppointmentSection()}
                {this.renderBookingSection()}
            </Container>
        );
    }

    renderPatientSection() {
        const {
            mainPatientName,
            phoneNumber,
            expandedPatientSection,
        } = this.props;
        const { showPatientSection } = this.state;

        const staticSection = (
            <ContentWrapper ref={this._staticPatientSectionRef}>
                <Field
                    data-test="patient-name-field"
                    className="exclude-from-playback"
                >
                    {mainPatientName}
                </Field>
                <Field
                    data-test="phone-number-field"
                    className="exclude-from-playback"
                >
                    {phoneNumber}
                </Field>
            </ContentWrapper>
        );

        return (
            <React.Fragment>
                {this.renderSectionHeader(
                    'Patient',
                    this.expandPatientSection,
                    showPatientSection
                )}
                {!showPatientSection ? (
                    staticSection
                ) : (
                    <Expandable
                        canAnimate={!this._openPatientSectionOnMount}
                        startHeight={this._staticPatientSectionHeight}
                    >
                        {expandedPatientSection}
                    </Expandable>
                )}
            </React.Fragment>
        );
    }

    renderInsuranceSection() {
        const {
            insuranceCarrierName,
            insurancePlanName,
            expandedInsuranceSection,
        } = this.props;
        const { showInsuranceSection } = this.state;

        const staticSection = (
            <ContentWrapper ref={this._staticInsuranceSectionRef}>
                {insuranceCarrierName && insurancePlanName ? (
                    <React.Fragment>
                        <Field data-test="insurance-carrier-field">
                            {insuranceCarrierName}
                        </Field>
                        <Field>{insurancePlanName}</Field>
                    </React.Fragment>
                ) : (
                    <Field>{IM_PAYING_MYSELF_TEXT}</Field>
                )}
                {this.renderMemberId()}
                <InsuranceCardSectionWrapper>
                    <InsuranceCardSection />
                </InsuranceCardSectionWrapper>
            </ContentWrapper>
        );

        return (
            <React.Fragment>
                {this.renderSectionHeader(
                    'Insurance',
                    this.expandInsuranceSection,
                    showInsuranceSection
                )}
                {!showInsuranceSection ? (
                    staticSection
                ) : (
                    <Expandable
                        canAnimate={!this._openInsuranceSectionOnMount}
                        startHeight={this._staticInsuranceSectionHeight}
                    >
                        {expandedInsuranceSection}
                    </Expandable>
                )}
            </React.Fragment>
        );
    }

    renderAppointmentSection() {
        const {
            expandedAppointmentSection,
            formattedAppointmentTime,
            isNewPatient,
            procedureName,
        } = this.props;

        const { showAppointmentSection } = this.state;

        const staticSection = (
            <ContentWrapper ref={this._staticAppointmentSectionRef}>
                <Field>Visit reason: {procedureName}</Field>
                <Field>
                    {isNewPatient ? 'New Patient' : 'Existing Patient'}
                </Field>
                <Field>{formattedAppointmentTime}</Field>
                {this.renderDoctorNotes()}
                {this.maybeRenderEmergencyNotice()}
            </ContentWrapper>
        );

        return (
            <React.Fragment>
                {this.renderSectionHeader(
                    'Appointment',
                    this.expandAppointmentSection,
                    showAppointmentSection
                )}
                {!showAppointmentSection ? (
                    staticSection
                ) : (
                    <Expandable
                        canAnimate
                        startHeight={this._staticAppointmentSectionHeight}
                    >
                        {expandedAppointmentSection}
                    </Expandable>
                )}
            </React.Fragment>
        );
    }

    renderBookingSection() {
        const {
            hasUsingInsuranceRequirement,
            onBookAppointmentClick,
        } = this.props;
        return (
            <React.Fragment>
                <Button
                    data-test="book-button"
                    fluid
                    onClick={onBookAppointmentClick}
                >
                    Book appointment
                </Button>
                {hasUsingInsuranceRequirement && (
                    <UsingInsuranceRequirementMessage>
                        {
                            'By booking this appointment, I certify that the \
                        insurance or payment selected is the one that I will be \
                        using when I see this medical professional, and that I have read and agree to the Zocdoc '
                        }
                        <Link href="https://www.zocdoc.com/about/terms">
                            terms of use
                        </Link>
                        .
                    </UsingInsuranceRequirementMessage>
                )}
            </React.Fragment>
        );
    }

    renderSectionHeader(title, expandCallback, isExpanded) {
        const uemSelector = `edit-${title.toLowerCase()}-details-link`;
        // In default scenario this is handled by a puntback
        return (
            <SectionHeaderWrapper>
                <SectionTitle>{title}</SectionTitle>
                <MetricsClickable element={`Edit${title}DetailsLink`}>
                    {!isExpanded && (
                        <EditLink
                            data-test={uemSelector}
                            data-uem-id={uemSelector}
                            onClick={expandCallback}
                        >
                            Edit
                        </EditLink>
                    )}
                </MetricsClickable>
            </SectionHeaderWrapper>
        );
    }

    renderMemberId() {
        const { isEditingMemberId } = this.state;
        return (
            <React.Fragment>
                {!isEditingMemberId && this.renderStaticMemberIdField()}
                <EditableMemberIdComponentWrapper isHidden={!isEditingMemberId}>
                    <AppointmentDetailsForm
                        {...this.props.appointmentDetails}
                        shouldShowInsuranceMemberIdInput={
                            !this.props.shouldDisableMemberIdField
                        }
                        shouldRenderAppointmentDetails={false}
                    />
                </EditableMemberIdComponentWrapper>
            </React.Fragment>
        );
    }

    renderStaticMemberIdField() {
        const {
            appointmentDetails: { insuranceId },
        } = this.props;
        if (insuranceId) {
            return (
                <Field
                    className="exclude-from-playback"
                    data-test="static-member-id-field"
                >
                    Member ID: {insuranceId}
                </Field>
            );
        } else if (this.hasInsurance()) {
            return (
                <MetricsClickable element="AddMemberIdLink">
                    <FieldLink
                        data-test="add-member-id-link"
                        onClick={this.handleClickAddMemberId}
                    >
                        Add your insurance member ID
                    </FieldLink>
                </MetricsClickable>
            );
        }
    }

    maybeRenderEmergencyNotice() {
        if (this.props.shouldShowEmergencyNotice) {
            return (
                <EmergencyNoticeWrapper>
                    <EmergencyNotice showEmergencyNotice />
                </EmergencyNoticeWrapper>
            );
        }
    }

    renderDoctorNotes() {
        if (this.state.isEditingDoctorNotes) {
            return <DoctorNotes />;
        } else {
            return (
                <MetricsClickable element="AddDoctorNotesLink">
                    <FieldLink
                        data-test="add-doctor-notes-link"
                        onClick={this.handleClickAddDoctorNotes}
                    >
                        Add a note for the doctor
                    </FieldLink>
                </MetricsClickable>
            );
        }
    }

    handleClickAddMemberId = () => {
        this.setState({
            isEditingMemberId: true,
        });
    };

    handleClickAddDoctorNotes = () => {
        this.setState({
            isEditingDoctorNotes: true,
        });
    };

    expandPatientSection = () => {
        if (this._staticPatientSectionRef.current) {
            // account for margin
            const startHeight =
                parseInt(
                    this._staticPatientSectionRef.current.scrollHeight,
                    10
                ) - 32;
            this._staticPatientSectionHeight = `${startHeight}px`;
        }
        this.setState({
            showPatientSection: true,
        });
    };

    expandInsuranceSection = () => {
        if (this._staticInsuranceSectionRef.current) {
            const startHeight =
                parseInt(
                    this._staticInsuranceSectionRef.current.scrollHeight,
                    10
                ) - 32;
            this._staticInsuranceSectionHeight = `${startHeight}px`;
        }
        this.setState({
            showInsuranceSection: true,
        });
    };

    expandAppointmentSection = () => {
        if (this._staticAppointmentSectionRef.current) {
            const startHeight =
                parseInt(
                    this._staticAppointmentSectionRef.current.scrollHeight,
                    10
                ) - 32;
            this._staticAppointmentSectionHeight = `${startHeight}px`;
        }
        this.setState({
            showAppointmentSection: true,
        });
    };

    hasInsurance = () => {
        const { insuranceCarrierId, insurancePlanId } = this.props;
        return (
            insurancePlanId &&
            parseInt(insurancePlanId, 10) > -1 &&
            insuranceCarrierId &&
            parseInt(insuranceCarrierId, 10) > -1
        );
    };
}

const Container = styled.div`
    max-width: 504px;
    ${FormRow} {
        margin: 12px 0 23px 0;
    }
`;

const SectionHeaderWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    padding-bottom: 12px;
    border-bottom: 1px solid ${slate40};
`;

const SectionTitle = styled.p`
    font: 18px ${FontFamily.SharpSansSemibold};
`;

const EditLink = styled.div`
    ${linkStyle}
    font: 14px ${FontFamily.SharpSansSemibold};
`;

const ContentWrapper = styled.div`
    padding: 8px 0 28px 0;
`;

const Field = styled.p`
    font: 14px/32px ${FontFamily.SharpSansMedium};
`;

const FieldLink = styled.div`
    ${linkStyle}
    display: inline-block;
    font: 14px/20px ${FontFamily.SharpSansSemibold};
    margin: 6px 0;
`;

const EmergencyNoticeWrapper = styled.div`
    margin: 20px 0 4px 0;
`;

const InsuranceCardSectionWrapper = styled.div`
    ${ShareMyInsuranceCardWrapper} {
        padding: 12px 0 4px 0;
        margin: 0;
    }
`;

const UsingInsuranceRequirementMessage = styled.p`
    margin-top: 16px;
    color: ${slate70};
    font: 13px/20px ${FontFamily.SharpSansMedium};
`;

const EditableMemberIdComponentWrapper = styled.div`
    display: ${p => (p.isHidden ? 'none' : 'block')};
`;

export default withCustomMetrics({
    section: 'StaticReviewAndBook',
    component: 'StaticReviewAndBook',
})(BaseStaticReviewAndBook);
